/**
 * A count as the Panel writes it: with a point every three digits from 1.000 («4.500 pedidos», «1.298», «2.160 h»),
 * the way its money is written (`money.ts`). `toLocaleString('es-ES')` and `Intl.NumberFormat('es-ES')` group only from
 * 10.000, so a count printed with them, or raw, reads «4500» beside «4.500,00 €». Every count a screen states goes
 * through here; a screen adds its own noun and its own word for a missing value.
 */

export interface CountFormatOptions {
  /** Digits after the comma («10,5 h»); none by default. The value is rounded half away from zero to them. */
  readonly decimals?: 0 | 1 | 2;
  /** A leading plus for a positive count; a negative one always carries U+2212. */
  readonly signed?: boolean;
}

/** Formats a count («4.500», «−12», «1.234,5»), or null when the value is not a finite number. */
export function formatCount(value: number, { decimals = 0, signed = false }: CountFormatOptions = {}): string | null {
  if (!Number.isFinite(value)) return null;
  const scale = 10 ** decimals;
  // Half away from zero, on the magnitude: `toFixed` alone rounds 0.5 and 2.5 differently.
  const rounded = Math.round(Math.abs(value) * scale + Number.EPSILON * scale) / scale;
  const [whole = '0', fraction] = rounded.toFixed(decimals).split('.');
  const grouped = whole.length < 4 ? whole : whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const sign = rounded === 0 ? '' : value < 0 ? '−' : signed ? '+' : '';
  return `${sign}${grouped}${fraction === undefined ? '' : `,${fraction}`}`;
}
